import { TAG_TYPES } from '@/lib/api/tags';
import { processServerError, handledError } from '@/lib/api/listQuery';
import { notifyOnSuccess } from '@/lib/api/notifyOnSuccess';
import { buildDeclarationFiltersInput } from '@/lib/mappers';
import {
  buildDeclarationsQuery,
  processDeclarationsPage,
  processDeclarationFilterOptions,
  processDeclarationOverview,
  processDeclarationDetailsTab,
  processDeclarationAllocations,
  buildEmissionsDto,
  processLinkedShipmentsForDeclaration,
  processDeclarationAttachments,
  processDeclarationEmissionCalculations,
  buildLinkedShipmentOrderBy,
  toDeclarationDetail,
  toValidateDeclarationDto,
  toEditDeclarationDto,
  buildDeclarationsFormData,
  processUploadedDeclarations,
  processAddedShipmentDeclarations,
  processShipmentForAddDeclarations,
} from '../mappers/declarationsMappers';
// Everything lives under the active module segment: /:module/inbound (iscc-backend inboundRoute).
const PATHS = {
  list: (module) => `/${module}/inbound`,
  upload: (module) => `/${module}/inbound/declarations/upload`,
  shipmentDeclarations: (module, shipmentId) => `/${module}/batch-group/${shipmentId}/declaration`,
  detail: (module, id) => `/${module}/inbound/${id}`,
  validate: (module, id) => `/${module}/inbound/validate/${id}`,
  // PROPOSED — no retry route exists yet; see the slice CLAUDE.md.
  retry: (module, id) => `/${module}/inbound/${id}/retry-extraction`,
};

// Add declarations ▸ Summary — which picked names the account already holds; up to 60.
export const TAKEN_DECLARATION_FILENAMES_QUERY = `
  query TakenDeclarationFilenames($filenames: [String!]!) {
    takenDeclarationFilenames(filenames: $filenames)
  }
`;

// Only the fields the Add declaration meta bar draws.
export const SHIPMENT_FOR_ADD_DECLARATIONS_QUERY = `
  query ShipmentForAddDeclarations($id: ID!) {
    shipment(id: $id) {
      id
      shipmentId
      material { name }
      sentBySupplier { companyname }
      receivedAtSite { siteName }
      multiSiteTransfer
      declarationUnit
      shipmentQuantityDeclaration
      availableShipmentQuantityDeclaration
      pendingShipmentQuantityDeclaration
      dateOfDispatch
      totalContractsLinked
      totalDeclarationsLinked
    }
  }
`;

// The filter panel's Material and Supplier choices — only what the table's declarations carry.
export const DECLARATION_FILTER_OPTIONS_QUERY = `
  query DeclarationFilterOptions($certificateType: CertificateType, $filters: DeclarationFilters) {
    declarationFilterOptions(certificateType: $certificateType, filters: $filters) {
      materials { id name }
      suppliers { id name }
    }
  }
`;

// The fields the Declarations table renders; `Declaration` has 50+.
export const DECLARATIONS_QUERY = `
  query Declarations($certificateType: CertificateType, $limit: Int, $page: Int, $search: DeclarationSearch, $filters: DeclarationFilters) {
    declarations(certificateType: $certificateType, limit: $limit, page: $page, search: $search, filters: $filters) {
      nodes {
        id
        referenceId
        declarationNo
        status
        declarationVerificationStatus
        declarationFile { id }
        material { name }
        site { siteName }
        backToBackAccount { accountName }
        sentBySupplier { companyname }
        sentBySite { siteName }
        sentByBackToBackAccount { accountName }
        shippingFrom
        terminal
        vessel
        workingUnit
        declarationUnit
        trackingUnit
        receivedQuantityWorking
        receivedQuantityDeclaration
        receivedQuantityTracking
        surplusQuantityWorking
        surplusQuantityDeclaration
        surplusQuantityTracking
        writeOffQuantityWorking
        writeOffQuantityDeclaration
        writeOffQuantityTracking
        adjustedQuantityWorking
        adjustedQuantityDeclaration
        adjustedQuantityTracking
        availableQuantityWorking
        availableQuantityDeclaration
        availableQuantityTracking
        ghgEmissions
        ghgEmissionSavings
        rawMaterialType
        countryOfOrigin
        feedstockCategory
        allocationPreference
        dateOfDischarge
        dateOfDispatch
        dateOfIssuance
        discrepancyCount
        discrepancies { type message attribute refKind contractId declarationId daysLeft }
      }
      pageInfo { total }
    }
  }
`;

// Everything <DeclarationForm> prefills, tab by tab; the mapper notes the two fields missing.
export const DECLARATION_QUERY = `
  query Declaration($id: ID!) {
    declaration(id: $id) {
      id
      referenceId
      status
      declarationVerificationStatus
      terminal
      terminalRefId
      multiSiteTransfer
      shipmentIds
      dateOfDischarge
      material { id }
      receivedAtSite { id }
      receivedAtBackToBackAccount { id }
      sentBySupplier { id }
      sentBySite { id }
      sentByBackToBackAccount { id }
      declarationFile { id filename }
      overview {
        sustainability { feedstockCategory allocationPreference }
      }
      declarationDetails {
        sdDetails {
          declarationNo
          dateOfIssuance
          dateOfDispatch
          supplierCertificateNumber
          declarationVerificationStatus
        }
        materialDetails {
          typeOfProduct
          typeOfRawMaterial
          additionalInformation
          countryOfOrigin
          quantity
          unit
          energyContent
          euRedCompliantMaterial
          isccCompliantMaterial
          chainOfCustodyOption
          countryOfBiofuelProduction
          startDateOfBiofuelProduction
          startDateOfBioliquidUse
          rawMaterialSustainabilityCriteria
          biomassIntermediateCrop
          biomassIlucRiskFeedstocks
          rawMaterialWasteDefinition
          wasteAnimalByProductPermitNumber
          supportForProduction
          supportNatureAndScheme
        }
        emissionDetails {
          ghgEmissionRed3
          totalGhgEmissions
          ghgEec
          ghgEl
          ghgEp
          ghgEtd
          ghgEu
          ghgEsca
          ghgEccs
          ghgEccr
          allocatedHeat
          allocatedElectricity
          escaBonusCap
          nuts2Area
          biofuelsForTransport
          bioliquidsBiomassFuelsElectricity
          biomassFuelsElectricityOutermostRegions
          bioliquidsBiomassFuelsUsefulHeat
          biomassFuelsUsefulHeatCoalSubstitution
          fssFoodSecurityStandard
          eudrDeforestationRegulation
          nonGmoFoodFeed
          nonGmoTechnicalMarkets
        }
      }
    }
  }
`;

// Declaration details ▸ the header every tab shares, plus the Overview tab it opens on.
export const DECLARATION_OVERVIEW_QUERY = `
  query DeclarationOverview($id: ID!) {
    declaration(id: $id) {
      id
      referenceId
      declarationNo
      status
      declarationVerificationStatus
      canFulfillOrder
      shipmentIds
      terminal
      dateOfIssuance
      dateOfDispatch
      dateOfDischarge
      declarationFile { id filename }
      material { name }
      sentBySupplier { companyname }
      sentBySite { siteName }
      sentByBackToBackAccount { accountName }
      receivedAtSite { siteName }
      receivedAtBackToBackAccount { accountName }
      receivedAtStorage { storageName }
      massBalancePeriod { id name startDate endDate status }
      discrepancyCount
      discrepancies { type message attribute refKind contractId declarationId daysLeft }
      overview {
        quantities {
          declarationUnit
          workingUnit
          trackingUnit
          receivedQuantityDeclaration
          receivedQuantityWorking
          receivedQuantityTracking
          surplusQuantityDeclaration
          surplusQuantityWorking
          surplusQuantityTracking
          writeOffQuantityDeclaration
          writeOffQuantityWorking
          writeOffQuantityTracking
          adjustedQuantityDeclaration
          adjustedQuantityWorking
          adjustedQuantityTracking
          availableQuantityDeclaration
          availableQuantityWorking
          availableQuantityTracking
        }
        sustainability {
          ghgEmissions
          ghgEmissionSavings
          rawMaterialType
          countryOfOrigin
          feedstockCategory
          allocationPreference
        }
        massBalancePeriodDetails {
          id
          name
          startDate
          endDate
          status
          declarationUnit
          workingUnit
          trackingUnit
          openingBalanceDeclaration
          openingBalanceWorking
          openingBalanceTracking
          inboundQuantityDeclaration
          inboundQuantityWorking
          inboundQuantityTracking
          surplusQuantityDeclaration
          surplusQuantityWorking
          surplusQuantityTracking
          writeOffQuantityDeclaration
          writeOffQuantityWorking
          writeOffQuantityTracking
          allocatedQuantityDeclaration
          allocatedQuantityWorking
          allocatedQuantityTracking
          closingBalanceDeclaration
          closingBalanceWorking
          closingBalanceTracking
        }
      }
    }
  }
`;

// Declaration details ▸ its own tab. One field per tab, so a tab fetches only what it draws.
export const DECLARATION_DETAILS_TAB_QUERY = `
  query DeclarationDetailsTab($id: ID!) {
    declaration(id: $id) {
      id
      declarationDetails {
        sdDetails {
          declarationNo
          dateOfIssuance
          dateOfDispatch
          supplierCertificateNumber
          declarationVerificationStatus
          note
        }
        materialDetails {
          typeOfProduct
          typeOfRawMaterial
          additionalInformation
          countryOfOrigin
          quantity
          unit
          trackingUnit
          density
          euRedCompliantMaterial
          isccCompliantMaterial
          chainOfCustodyOption
          countryOfBiofuelProduction
          startDateOfBiofuelProduction
          startDateOfBioliquidUse
          rawMaterialSustainabilityCriteria
          biomassIntermediateCrop
          biomassIlucRiskFeedstocks
          rawMaterialWasteDefinition
          wasteAnimalByProductPermitNumber
          supportForProduction
          supportNatureAndScheme
        }
        emissionDetails {
          ghgEmissionRed3
          totalGhgEmissions
          ghgEec
          ghgEl
          ghgEp
          ghgEtd
          ghgEu
          ghgEsca
          ghgEccs
          ghgEccr
          allocatedHeat
          allocatedElectricity
          escaBonusCap
          nuts2Area
          biofuelsForTransport
          bioliquidsBiomassFuelsElectricity
          biomassFuelsElectricityOutermostRegions
          bioliquidsBiomassFuelsUsefulHeat
          biomassFuelsUsefulHeatCoalSubstitution
          fssFoodSecurityStandard
          eudrDeforestationRegulation
          nonGmoFoodFeed
          nonGmoTechnicalMarkets
        }
      }
    }
  }
`;

// Declaration details ▸ Allocation details. The field takes no arguments — it is the whole list.
export const DECLARATION_ALLOCATIONS_QUERY = `
  query DeclarationAllocations($id: ID!) {
    declaration(id: $id) {
      id
      # The rows carry amounts but no unit strings, so the declaration's own name them.
      overview {
        quantities {
          declarationUnit
          workingUnit
          trackingUnit
        }
      }
      allocationDetails {
        transactionId
        transactionType
        soldTo { companyname }
        massBalancePeriod { id name startDate endDate }
        allocatedQuota
        allocatedQuantityDeclaration
        allocatedQuantityWorking
        allocatedQuantityTracking
        allocatedAt
      }
    }
  }
`;

// Declaration details ▸ Linked incoming shipments — top-level, so it takes the list's own args.
export const LINKED_SHIPMENTS_FOR_DECLARATION_QUERY = `
  query LinkedShipmentsForDeclaration($declarationId: ID!, $orderBy: [ShipmentOrderBy!]) {
    linkedShipmentsForDeclaration(declarationId: $declarationId, limit: 100, page: 1, orderBy: $orderBy) {
      nodes {
        assignedQuantityWorking
        assignedQuantityDeclaration
        assignedQuantityTracking
        linkedContractIds
        shipment {
          id
          shipmentId
          terminal
          status
          workingUnit
          declarationUnit
          trackingUnit
          shipmentQuantityWorking
          shipmentQuantityDeclaration
          shipmentQuantityTracking
          unassignedContractQuantityWorking
          unassignedContractQuantityDeclaration
          unassignedContractQuantityTracking
          pendingShipmentQuantityWorking
          pendingShipmentQuantityDeclaration
          pendingShipmentQuantityTracking
          availableShipmentQuantityWorking
          availableShipmentQuantityDeclaration
          availableShipmentQuantityTracking
          ghgEmissions
          ghgEmissionSavings
          rawMaterialType
          countryOfOrigin
          feedstockCategory
          preferredAllocation
          dateOfDispatch
          discrepancyCount
          discrepancies { type message }
        }
      }
      pageInfo { total }
    }
  }
`;

// Declaration details ▸ Attachments — the resolver folds in the shipment's files (doc p.17).
export const DECLARATION_ATTACHMENTS_QUERY = `
  query DeclarationAttachments($id: ID!) {
    declaration(id: $id) {
      id
      attachments(limit: 100, page: 1) {
        nodes {
          id
          filename
          attachmentType
          createdAt
          updatedAt
        }
        pageInfo { total }
      }
    }
  }
`;

// Declaration details ▸ Emission calculations. Same type as `declarationDetails.emissionDetails`.
export const DECLARATION_EMISSION_CALCULATIONS_QUERY = `
  query DeclarationEmissionCalculations($id: ID!) {
    declaration(id: $id) {
      id
      sentBySupplier { id }
      receivedAtSite { id siteName }
      emissionCalculations {
        isEditable
        totalTransportationEmissions
        ghgEmissionRed3
        totalGhgEmissions
        ghgEec
        ghgEl
        ghgEp
        ghgEtd
        ghgEu
        ghgEsca
        ghgEccs
        ghgEccr
        allocatedHeat
        allocatedElectricity
        escaBonusCap
        nuts2Area
        biofuelsForTransport
        bioliquidsBiomassFuelsElectricity
        biomassFuelsElectricityOutermostRegions
        bioliquidsBiomassFuelsUsefulHeat
        biomassFuelsUsefulHeatCoalSubstitution
        fssFoodSecurityStandard
        eudrDeforestationRegulation
        nonGmoFoodFeed
        nonGmoTechnicalMarkets
      }
    }
  }
`;

const EMISSION_PATHS = {
  emissions: (module, id) => `/${module}/inbound/emissions/${id}`,
};

export const declarationsEndpoints = (build) => ({
  getIncomingDeclarations: build.query({
    // `document` routes this through GraphQL; the module scopes it as `certificateType`.
    query: (screenState) => ({
      document: DECLARATIONS_QUERY,
      variables: buildDeclarationsQuery(screenState),
    }),
    transformResponse: (data) => processDeclarationsPage(data.declarations),
    providesTags: (result) =>
      result
        ? [
            ...result.rows.map((row) => ({ type: TAG_TYPES.Declaration, id: row.id })),
            { type: TAG_TYPES.Declaration, id: 'LIST' },
          ]
        : [{ type: TAG_TYPES.Declaration, id: 'LIST' }],
  }),

  // Takes the page's fixed scope only — a picked filter here would hide its siblings.
  getDeclarationFilterOptions: build.query({
    query: ({ certificateType, extraFilters }) => ({
      document: DECLARATION_FILTER_OPTIONS_QUERY,
      variables: { certificateType, filters: buildDeclarationFiltersInput({}, extraFilters) },
    }),
    transformResponse: (data) => processDeclarationFilterOptions(data.declarationFilterOptions),
    providesTags: [{ type: TAG_TYPES.Declaration, id: 'LIST' }],
  }),

  getDeclaration: build.query({
    query: ({ id }) => ({ document: DECLARATION_QUERY, variables: { id } }),
    transformResponse: (data) => toDeclarationDetail(data.declaration),
    transformErrorResponse: handledError,
    providesTags: (result, error, { id }) => [{ type: TAG_TYPES.Declaration, id }],
  }),

  // The detail page's header + Overview tab; the form reads `getDeclaration` instead.
  getDeclarationOverview: build.query({
    query: ({ id }) => ({ document: DECLARATION_OVERVIEW_QUERY, variables: { id } }),
    transformResponse: (data) => processDeclarationOverview(data.declaration),
    transformErrorResponse: handledError,
    providesTags: (result, error, { id }) => [{ type: TAG_TYPES.Declaration, id }],
  }),

  getDeclarationDetailsTab: build.query({
    query: ({ id }) => ({ document: DECLARATION_DETAILS_TAB_QUERY, variables: { id } }),
    transformResponse: (data) => processDeclarationDetailsTab(data.declaration),
    transformErrorResponse: handledError,
    providesTags: (result, error, { id }) => [{ type: TAG_TYPES.Declaration, id }],
  }),

  getLinkedShipmentsForDeclaration: build.query({
    query: ({ declarationId, sort }) => ({
      document: LINKED_SHIPMENTS_FOR_DECLARATION_QUERY,
      variables: { declarationId, orderBy: buildLinkedShipmentOrderBy(sort) },
    }),
    transformResponse: (data) =>
      processLinkedShipmentsForDeclaration(data.linkedShipmentsForDeclaration),
    transformErrorResponse: handledError,
    providesTags: (result, error, { declarationId }) => [
      { type: TAG_TYPES.Declaration, id: declarationId },
    ],
  }),

  getDeclarationEmissionCalculations: build.query({
    query: ({ id }) => ({ document: DECLARATION_EMISSION_CALCULATIONS_QUERY, variables: { id } }),
    transformResponse: (data) => processDeclarationEmissionCalculations(data.declaration),
    transformErrorResponse: handledError,
    providesTags: (result, error, { id }) => [{ type: TAG_TYPES.Declaration, id }],
  }),

  getDeclarationAttachments: build.query({
    query: ({ id }) => ({ document: DECLARATION_ATTACHMENTS_QUERY, variables: { id } }),
    transformResponse: (data) => processDeclarationAttachments(data.declaration),
    transformErrorResponse: handledError,
    providesTags: (result, error, { id }) => [{ type: TAG_TYPES.Declaration, id }],
  }),

  updateDeclarationEmissions: build.mutation({
    query: ({ module, id, values }) => ({
      url: EMISSION_PATHS.emissions(module, id),
      method: 'PUT',
      body: buildEmissionsDto(values),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('DECLARATIONS_EMISSIONS_SAVED'),
    invalidatesTags: (result, error, { id }) => [{ type: TAG_TYPES.Declaration, id }],
  }),

  deleteDeclarationEmissions: build.mutation({
    query: ({ module, id }) => ({
      url: EMISSION_PATHS.emissions(module, id),
      method: 'DELETE',
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('DECLARATIONS_EMISSIONS_DELETED'),
    invalidatesTags: (result, error, { id }) => [{ type: TAG_TYPES.Declaration, id }],
  }),

  getDeclarationAllocations: build.query({
    query: ({ id }) => ({ document: DECLARATION_ALLOCATIONS_QUERY, variables: { id } }),
    transformResponse: (data) => processDeclarationAllocations(data.declaration),
    transformErrorResponse: handledError,
    providesTags: (result, error, { id }) => [{ type: TAG_TYPES.Declaration, id }],
  }),

  validateDeclaration: build.mutation({
    query: ({ module, id, values, materials }) => ({
      url: PATHS.validate(module, id),
      method: 'POST',
      body: toValidateDeclarationDto(values, materials),
    }),
    invalidatesTags: (result, error, { id }) => [
      { type: TAG_TYPES.Declaration, id },
      { type: TAG_TYPES.Declaration, id: 'LIST' },
    ],
  }),

  // A COMPLETED declaration only — validate/:id stays DRAFT-only (iscc-backend).
  editDeclaration: build.mutation({
    query: ({ module, id, values, materials }) => ({
      url: PATHS.detail(module, id),
      method: 'PATCH',
      body: toEditDeclarationDto(values, materials),
    }),
    invalidatesTags: (result, error, { id }) => [
      { type: TAG_TYPES.Declaration, id },
      { type: TAG_TYPES.Declaration, id: 'LIST' },
    ],
  }),

  // Asked fresh on every Summary visit — another tab may have saved one since.
  getTakenDeclarationFilenames: build.query({
    query: (filenames) => ({
      document: TAKEN_DECLARATION_FILENAMES_QUERY,
      variables: { filenames },
    }),
    transformResponse: (data) => data.takenDeclarationFilenames ?? [],
    keepUnusedDataFor: 0,
  }),

  // The server 400s a declaration already in use — the menu disables it for the same rule.
  deleteDeclaration: build.mutation({
    query: ({ module, id }) => ({ url: PATHS.detail(module, id), method: 'DELETE' }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('DECLARATIONS_DELETED'),
    invalidatesTags: (result, error, { id }) => [
      { type: TAG_TYPES.Declaration, id },
      { type: TAG_TYPES.Declaration, id: 'LIST' },
      { type: TAG_TYPES.Shipment, id: 'LIST' },
    ],
  }),

  /**
   * Re-queues a PROCESSING declaration for PDF extraction. Extraction is what moves a row
   * out of PROCESSING, so a lost publish strands it there — this is the way back.
   * The route is PROPOSED; the FE is otherwise complete (slice CLAUDE.md).
   */
  retryDeclarationExtraction: build.mutation({
    query: ({ module, id }) => ({ url: PATHS.retry(module, id), method: 'POST' }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('DECLARATIONS_RETRY_QUEUED'),
    invalidatesTags: (result, error, arg) => [
      { type: TAG_TYPES.Declaration, id: arg.id },
      { type: TAG_TYPES.Declaration, id: 'LIST' },
    ],
  }),

  getShipmentForAddDeclarations: build.query({
    query: (id) => ({ document: SHIPMENT_FOR_ADD_DECLARATIONS_QUERY, variables: { id } }),
    transformResponse: (data) => processShipmentForAddDeclarations(data.shipment),
    providesTags: (result, error, id) => [{ type: TAG_TYPES.Shipment, id }],
  }),

  // Every file in ONE multipart request; the server takes up to MAX_DECLARATION_FILES.
  uploadDeclarations: build.mutation({
    query: ({ module, files }) => ({
      url: PATHS.upload(module),
      method: 'POST',
      body: buildDeclarationsFormData(files),
    }),
    transformResponse: processUploadedDeclarations,
    transformErrorResponse: processServerError,
    invalidatesTags: [{ type: TAG_TYPES.Declaration, id: 'LIST' }],
  }),

  // The shipment supplies material and site, so the body carries only files.
  addDeclarationsToShipment: build.mutation({
    query: ({ module, shipmentId, files }) => ({
      url: PATHS.shipmentDeclarations(module, shipmentId),
      method: 'POST',
      body: buildDeclarationsFormData(files),
    }),
    transformResponse: processAddedShipmentDeclarations,
    transformErrorResponse: processServerError,
    invalidatesTags: (result, error, arg) => [
      { type: TAG_TYPES.Declaration, id: 'LIST' },
      { type: TAG_TYPES.Shipment, id: arg.shipmentId },
      { type: TAG_TYPES.Shipment, id: 'LIST' },
    ],
  }),
});
